import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { TriangleAlert } from 'lucide-react';
import {
  formatIqd,
  fromIqd,
  fromMillidinars,
  subtract,
  type CustomerAccountDto,
  type Millidinars,
  type RefillReceiptDto,
} from '@gc/shared';

import { useAuth } from '@/features/auth/useAuth';
import { api, ApiError } from '@/lib/api';
import { Row, Shell } from './RefillDialog';
import { useRefillOptions } from './useCustomers';

interface BalanceRefundDialogProps {
  customer: CustomerAccountDto;
  currencySymbol: string;
  onClose: () => void;
  onDone: () => void;
}

/**
 * Balance Refund — SPEC §5.5.
 *
 * Two independent gates, and this dialog shows which one is closed rather than
 * hiding the action: a cashier who cannot find the button assumes the software
 * is broken, whereas a disabled button naming the setting sends them to the
 * right place. Both gates are re-checked server-side; nothing here is trusted.
 */
export function BalanceRefundDialog({
  customer,
  currencySymbol,
  onClose,
  onDone,
}: BalanceRefundDialogProps) {
  const { t } = useTranslation();
  const { can } = useAuth();
  const { data: options } = useRefillOptions(true);

  const [amountIqd, setAmountIqd] = useState('');
  const [reason, setReason] = useState('');
  const [receipt, setReceipt] = useState<RefillReceiptDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const money = (value: Millidinars) => formatIqd(value, { currencySymbol });
  const balance = fromMillidinars(customer.balanceMd);
  const amount = fromIqd(Math.max(0, Math.floor(Number(amountIqd) || 0)));
  const remaining = subtract(balance, amount);

  // Both keys the route wants (2026-09-24): this privilege, and
  // `refunds.process` — the venue-wide "may give money back". Either missing
  // is the same refusal, so the sentence names the two.
  const hasPermission = can('customers.refundBalance') && can('refunds.process');
  const settingOn = options?.enableBalanceRefund ?? false;
  const blockedReason = !hasPermission
    ? t('customers.refund.noPermission')
    : options && !settingOn
      ? t('customers.refund.disabled')
      : null;

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      setReceipt(
        await api.post<RefillReceiptDto>(`/customers/${customer.id}/balance-refund`, {
          amountIqd: Math.floor(Number(amountIqd) || 0),
          reason: reason.trim(),
        }),
      );
      onDone();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : t('customers.refund.failed'));
    } finally {
      setSubmitting(false);
    }
  }

  const inputClass =
    'w-full rounded-control border border-subtle bg-input px-3 py-2 text-content outline-none focus:border-primary';

  if (receipt) {
    return (
      <Shell label={t('customers.refund.done')} onClose={onClose}>
        <h2 className="text-lg font-semibold text-content">{t('customers.refund.done')}</h2>
        <dl className="tabular mt-4 rounded-card bg-raised p-3 text-sm">
          <Row
            label={t('customers.refund.amount')}
            value={money(fromMillidinars(receipt.amountMd))}
            strong
          />
          <Row
            label={t('customers.balance')}
            value={money(fromMillidinars(receipt.balanceAfterMd))}
          />
        </dl>
        <button
          type="button"
          onClick={onClose}
          className="mt-4 w-full rounded-full bg-primary py-2.5 text-sm font-semibold text-primary-fg hover:bg-primary-hover"
        >
          {t('common.confirm')}
        </button>
      </Shell>
    );
  }

  return (
    <Shell label={t('customers.refund.title')} onClose={onClose}>
      <form onSubmit={(event) => void handleSubmit(event)}>
        <h2 className="text-lg font-semibold text-content">
          {t('customers.refund.title')} — {customer.fullName}
        </h2>

        {blockedReason ? (
          <p
            role="alert"
            className="mt-3 flex items-start gap-2 rounded-card border border-subtle bg-raised p-3 text-sm text-content-secondary"
          >
            <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0 text-ending" aria-hidden />
            <span>{blockedReason}</span>
          </p>
        ) : null}

        <label className="mt-4 block">
          <span className="mb-1 block text-sm text-content-secondary">
            {t('customers.refund.amount')}
          </span>
          <input
            type="number"
            min="1"
            step="1"
            max={Number(balance / 1000n)}
            required
            disabled={blockedReason != null}
            value={amountIqd}
            onChange={(event) => setAmountIqd(event.target.value)}
            className={`${inputClass} tabular`}
          />
        </label>

        <label className="mt-3 block">
          <span className="mb-1 block text-sm text-content-secondary">
            {t('customers.refund.reason')}
          </span>
          <input
            required
            maxLength={300}
            disabled={blockedReason != null}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            className={inputClass}
          />
          <span className="mt-1 block text-xs text-content-muted">
            {t('customers.refund.reasonHint')}
          </span>
        </label>

        <dl className="tabular mt-4 rounded-card bg-raised p-3 text-sm">
          <Row label={t('customers.refund.available')} value={money(balance)} />
          <Row label={t('customers.balance')} value={money(remaining)} strong />
        </dl>

        {/* Cash leaves the drawer, so the box goes DOWN. Stating it here keeps
            the box screen's negative entry from looking like an error. */}
        <p className="mt-2 text-xs text-content-secondary">{t('customers.refund.boxNotice')}</p>

        {error ? (
          <p role="alert" className="mt-3 text-sm text-danger">
            {error}
          </p>
        ) : null}

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-subtle px-4 py-2 text-sm text-content-secondary hover:bg-hover"
          >
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            disabled={
              submitting ||
              blockedReason != null ||
              amount <= 0n ||
              amount > balance ||
              reason.trim().length === 0
            }
            className="rounded-full bg-danger px-4 py-2 text-sm font-medium text-primary-fg hover:bg-danger-hover disabled:cursor-not-allowed disabled:opacity-40"
          >
            {submitting ? t('common.loading') : t('customers.refund.submit')}
          </button>
        </div>
      </form>
    </Shell>
  );
}
